import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { draftInternalMemoWithAi } from '../../lib/memoAiDraft';
import type { MemoAiPortalLookups } from '../../lib/memoAiLookups';
import { isMemoBodyEmpty } from '../../lib/memoBody';
import { fetchPostedMemosForAiStyle } from '../../lib/memoSubmissions';

type Props = {
  open: boolean;
  onClose: () => void;
  to: string;
  cc: string;
  from: string;
  date: string;
  currentSubject: string;
  currentBody: string;
  /** When drafting from an existing memo, exclude it from style references. */
  excludeMemoId?: string;
  onApply: (draft: { subject: string; bodyHtml: string }) => void;
};

export function MemoAiDraftModal({
  open,
  onClose,
  to,
  cc,
  from,
  date,
  currentSubject,
  currentBody,
  excludeMemoId,
  onApply,
}: Props) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [lookups, setLookups] = useState<MemoAiPortalLookups | null>(null);

  const resetAndClose = () => {
    if (busy) return;
    setError(null);
    setPreviewSubject('');
    setPreviewBody('');
    setLookups(null);
    onClose();
  };

  const handleGenerate = async () => {
    setBusy(true);
    setError(null);
    try {
      const styleReferences = await fetchPostedMemosForAiStyle({
        notesHint: notes,
        excludeId: excludeMemoId,
        candidateLimit: 2,
      }).catch(() => []);

      const draft = await draftInternalMemoWithAi({
        notes,
        to,
        cc,
        from,
        date,
        styleReferences,
      });
      setPreviewSubject(draft.subject);
      setPreviewBody(draft.bodyHtml);
      setLookups(draft.lookups);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate draft.');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = () => {
    if (!previewSubject.trim() || !previewBody.trim()) return;
    const hasExisting =
      Boolean(currentSubject.trim()) || !isMemoBodyEmpty(currentBody);
    if (
      hasExisting &&
      !window.confirm('Replace the current Subject and Body with this AI draft?')
    ) {
      return;
    }
    onApply({ subject: previewSubject.trim(), bodyHtml: previewBody });
    setNotes('');
    setPreviewSubject('');
    setPreviewBody('');
    setLookups(null);
    setError(null);
    onClose();
  };

  const hasLookupHits =
    lookups &&
    (lookups.customers.length > 0 ||
      lookups.salespersons.length > 0 ||
      lookups.items.length > 0 ||
      lookups.unresolved_customer_codes.length > 0 ||
      lookups.unresolved_item_codes.length > 0);

  return (
    <Modal title="AI draft memorandum" open={open} onClose={resetAndClose} wide>
      <div className="memo-ai-draft">
        <p className="field-hint">
          Paste the facts you want covered. The portal looks up customers, salespersons, items and
          DEM sales prices, and automatically mirrors the tone of similar Posted memoranda.
        </p>

        <label className="field">
          <span className="field-label">Your notes</span>
          <textarea
            className="field-input memo-ai-draft__notes"
            value={notes}
            disabled={busy}
            rows={7}
            placeholder="Example: Reassign C0477 from SA4 to SA6. Or: Advise new sales price for item XXXX at $1.20 effective next week."
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {error ? <div className="alert alert-error">{error}</div> : null}

        {hasLookupHits ? (
          <div className="memo-ai-draft__lookups">
            <p className="memo-ai-draft__preview-label">From portal database</p>
            {lookups.customers.length > 0 ? (
              <ul className="memo-ai-draft__lookup-list">
                {lookups.customers.map((c) => (
                  <li key={c.code}>
                    <strong>{c.code}</strong> {c.name}
                    {c.salesperson_label ? (
                      <span className="memo-ai-draft__lookup-meta">
                        {' '}
                        · {c.salesperson_label}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {lookups.items.length > 0 ? (
              <ul className="memo-ai-draft__lookup-list">
                {lookups.items.map((item) => (
                  <li key={item.code}>
                    <strong>{item.code}</strong> {item.description}
                    {item.uom ? (
                      <span className="memo-ai-draft__lookup-meta"> · {item.uom}</span>
                    ) : null}
                    {item.packing ? (
                      <span className="memo-ai-draft__lookup-meta">
                        {' '}
                        · packing {item.packing}
                      </span>
                    ) : null}
                    {item.sales_price ? (
                      <span className="memo-ai-draft__lookup-meta">
                        {' '}
                        · {item.sales_price}
                        {item.price_note ? ` (${item.price_note})` : ''}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {lookups.salespersons.length > 0 ? (
              <ul className="memo-ai-draft__lookup-list">
                {lookups.salespersons.map((s) => (
                  <li key={s.code}>{s.label}</li>
                ))}
              </ul>
            ) : null}
            {lookups.unresolved_customer_codes.length > 0 ||
            lookups.unresolved_item_codes.length > 0 ? (
              <p className="field-hint memo-ai-draft__lookup-miss">
                Not found:{' '}
                {[
                  ...lookups.unresolved_customer_codes,
                  ...lookups.unresolved_item_codes,
                ].join(', ')}
              </p>
            ) : null}
          </div>
        ) : null}

        {previewSubject || previewBody ? (
          <div className="memo-ai-draft__preview">
            <p className="memo-ai-draft__preview-label">Preview</p>
            <p className="memo-ai-draft__preview-subject">{previewSubject}</p>
            <div
              className="memo-ai-draft__preview-body"
              dangerouslySetInnerHTML={{ __html: previewBody }}
            />
          </div>
        ) : null}

        <div className="crm-form__actions">
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={resetAndClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy || !notes.trim()}
            onClick={() => void handleGenerate()}
          >
            {busy ? 'Looking up & drafting…' : previewSubject ? 'Regenerate' : 'Generate'}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !previewSubject.trim() || !previewBody.trim()}
            onClick={handleApply}
          >
            Apply to form
          </button>
        </div>
      </div>
    </Modal>
  );
}
